import React from 'react'
import { Link } from 'react-router-dom'

const variantClasses = {
  primary: 'btn-primary',
  secondary: 'btn-secondary',
  danger: 'btn-danger',
  success: 'btn-success',
  hero: 'btn-hero',
  'hero-secondary': 'btn-hero-secondary',
  ghost:
    'py-3 px-6 rounded-full border border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300 font-semibold transition-all',
  pagination:
    'px-4 py-2 rounded-full border border-gray-200 bg-white/80 backdrop-blur-sm hover:bg-white hover:shadow-md font-medium text-gray-600 transition-all',
  'pagination-active':
    'px-4 py-2 rounded-full bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-md shadow-primary-500/25 font-medium transition-all',
  toggle: 'px-4 py-2 rounded-full font-semibold transition-all duration-200',
  'toggle-active':
    'px-4 py-2 rounded-full bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-lg shadow-primary-500/30 font-semibold transition-all duration-200 scale-105',
  'toggle-inactive':
    'px-4 py-2 rounded-full bg-white/80 text-gray-600 hover:bg-white hover:shadow-md border border-gray-200/60 backdrop-blur-sm hover:scale-105 font-semibold transition-all duration-200',
  'tab-active': 'px-4 py-2 rounded-lg bg-primary-600 text-white font-semibold transition',
  'tab-inactive':
    'px-4 py-2 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 font-semibold transition',
  'tab-success':
    'px-4 py-2 rounded-lg bg-green-100 text-green-700 border border-green-300 font-semibold transition',
  outline:
    'py-3 px-4 rounded-lg bg-white border-2 border-gray-300 hover:border-primary-500 text-gray-700 font-semibold transition',
}

const sizeClasses = {
  sm: 'px-4 py-2 text-sm',
  md: '', // uses the CSS utility default (py-3 px-6)
  lg: 'px-8 py-3',
}

/**
 * Reusable Button component with Mountain Glass design system variants.
 *
 * @param {'primary'|'secondary'|'danger'|'success'|'hero'|'hero-secondary'|'ghost'|'pagination'|'pagination-active'|'toggle-active'|'toggle-inactive'} variant
 * @param {'sm'|'md'|'lg'} size
 * @param {boolean} fullWidth
 * @param {boolean} loading
 * @param {React.ReactNode} icon - Left icon element
 * @param {string} className - Additional classes
 * @param {string} to - If provided, renders a react-router Link instead of a button
 * @param {string} href - If provided, renders an anchor tag
 */
const Button = React.forwardRef(
  (
    {
      variant = 'primary',
      size = 'md',
      fullWidth = false,
      loading = false,
      disabled = false,
      icon,
      children,
      className = '',
      to,
      href,
      ...props
    },
    ref,
  ) => {
    const classes = [
      variantClasses[variant] || variantClasses.primary,
      sizeClasses[size] || '',
      fullWidth ? 'w-full' : '',
      icon || loading ? 'inline-flex items-center justify-center gap-2' : '',
      disabled || loading ? 'disabled:opacity-50 disabled:cursor-not-allowed' : '',
      className,
    ]
      .filter(Boolean)
      .join(' ')

    const content = (
      <>
        {loading ? (
          <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
            />
          </svg>
        ) : icon ? (
          icon
        ) : null}
        {children}
      </>
    )

    if (to) {
      return (
        <Link ref={ref} to={to} className={classes} {...props}>
          {content}
        </Link>
      )
    }

    if (href) {
      return (
        <a ref={ref} href={href} className={classes} {...props}>
          {content}
        </a>
      )
    }

    return (
      <button ref={ref} type="button" className={classes} disabled={disabled || loading} {...props}>
        {content}
      </button>
    )
  },
)

Button.displayName = 'Button'

export default Button
